import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Grid,
  Pagination,
  Segment,
  Statistic,
  Table,
  Tab,
} from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { API, isAdmin, showError, timestamp2string } from '../../helpers';
import { ITEMS_PER_PAGE } from '../../constants';

const ApiBill = () => {
  const { t } = useTranslation();
  const admin = isAdmin();
  const [activeTab, setActiveTab] = useState(0); // 0=B账单(客户), 1=A账单(成本)
  
  // B 账单（客户账单）
  const [billsB, setBillsB] = useState([]);
  const [statsB, setStatsB] = useState(null);
  const [loadingB, setLoadingB] = useState(false);
  const [activePageB, setActivePageB] = useState(1);
  const [totalB, setTotalB] = useState(0);

  // A 账单（成本账单）
  const [billsA, setBillsA] = useState([]);
  const [statsA, setStatsA] = useState(null);
  const [loadingA, setLoadingA] = useState(false);
  const [activePageA, setActivePageA] = useState(1);
  const [totalA, setTotalA] = useState(0);

  const [filters, setFilters] = useState({
    username: '',
    model_name: '',
    start_timestamp: Math.floor(new Date(new Date().setDate(new Date().getDate() - 30)).getTime() / 1000),
    end_timestamp: Math.floor(Date.now() / 1000),
  });

  // 加载 B 账单统计
  const loadStatsB = async () => {
    try {
      const params = new URLSearchParams({
        start_timestamp: filters.start_timestamp,
        end_timestamp: filters.end_timestamp,
        model_name: filters.model_name,
        bill_type: 'B',
      });
      if (admin) params.append('username', filters.username);

      const url = admin
        ? `/api/bills/api-stat?${params}`
        : `/api/bills/api-self-stat?${params}`;
      const res = await API.get(url);
      if (res.data.success) {
        setStatsB(res.data.data);
      }
    } catch (e) {
      showError(e.message);
    }
  };

  // 加载 A 账单统计
  const loadStatsA = async () => {
    try {
      const params = new URLSearchParams({
        start_timestamp: filters.start_timestamp,
        end_timestamp: filters.end_timestamp,
        model_name: filters.model_name,
        bill_type: 'A',
      });
      if (admin) params.append('username', filters.username);

      const url = admin
        ? `/api/bills/api-stat?${params}`
        : `/api/bills/api-self-stat?${params}`;
      const res = await API.get(url);
      if (res.data.success) {
        setStatsA(res.data.data);
      }
    } catch (e) {
      showError(e.message);
    }
  };

  // 加载 B 账单数据
  const loadBillsB = async (page = 1) => {
    setLoadingB(true);
    try {
      const params = new URLSearchParams({
        p: page - 1,
        bill_type: 'B',
        model_name: filters.model_name,
        start_timestamp: filters.start_timestamp,
        end_timestamp: filters.end_timestamp,
      });
      if (admin) {
        params.append('username', filters.username);
      }

      const apiPath = admin ? '/api/bills/api' : '/api/bills/api-self';
      const res = await API.get(`${apiPath}?${params}`);
      if (res.data.success) {
        setBillsB(res.data.data || []);
        setTotalB(res.data.total || 0);
      } else {
        showError(res.data.message);
      }
    } catch (e) {
      showError(e.message);
    }
    setLoadingB(false);
  };

  // 加载 A 账单数据
  const loadBillsA = async (page = 1) => {
    setLoadingA(true);
    try {
      const params = new URLSearchParams({
        p: page - 1,
        bill_type: 'A',
        model_name: filters.model_name,
        start_timestamp: filters.start_timestamp,
        end_timestamp: filters.end_timestamp,
      });
      if (admin) {
        params.append('username', filters.username);
      }

      const apiPath = admin ? '/api/bills/api' : '/api/bills/api-self';
      const res = await API.get(`${apiPath}?${params}`);
      if (res.data.success) {
        setBillsA(res.data.data || []);
        setTotalA(res.data.total || 0);
      } else {
        showError(res.data.message);
      }
    } catch (e) {
      showError(e.message);
    }
    setLoadingA(false);
  };

  // 计算总页数
  const getTotalPagesB = () => {
    return Math.ceil(totalB / ITEMS_PER_PAGE);
  };

  const getTotalPagesA = () => {
    return Math.ceil(totalA / ITEMS_PER_PAGE);
  };

  useEffect(() => {
    loadStatsB();
    loadStatsA();
    loadBillsB(1);
    loadBillsA(1);
  }, []);

  const handleSearch = () => {
    setActivePageB(1);
    setActivePageA(1);
    loadStatsB();
    loadStatsA();
    loadBillsB(1);
    loadBillsA(1);
  };

  const handlePageChangeB = (e, { activePage }) => {
    setActivePageB(activePage);
    loadBillsB(activePage);
  };

  const handlePageChangeA = (e, { activePage }) => {
    setActivePageA(activePage);
    loadBillsA(activePage);
  };

  // 渲染统计卡片
  const renderStats = (stats, billType) => {
    if (!stats) return null;
    
    const title = billType === 'B' ? t('bill.api.stats_title_b') : t('bill.api.stats_title_a');
    const profit = statsB && statsA ? (statsB.total_quota || 0) - (statsA.total_quota || 0) : 0;

    return (
      <Segment style={{ marginBottom: '1em' }}>
        <h4 style={{ marginTop: 0, color: '#666' }}>{title}</h4>
        <Grid columns={admin && billType === 'B' && statsA ? 3 : 2} textAlign='center' stackable>
          <Grid.Column>
            <Statistic size='small' color='green'>
              <Statistic.Value>¥{(stats.total_quota || 0).toFixed(4)}</Statistic.Value>
              <Statistic.Label>{billType === 'B' ? t('bill.api.customer_spending') : t('bill.api.cost_spending')}</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column>
            <Statistic size='small' color='blue'>
              <Statistic.Value>{stats.request_count || 0}</Statistic.Value>
              <Statistic.Label>{t('bill.api.request_count')}</Statistic.Label>
            </Statistic>
          </Grid.Column>
          {admin && billType === 'B' && statsA && (
            <Grid.Column>
              <Statistic size='small' color={profit >= 0 ? 'teal' : 'red'}>
                <Statistic.Value>¥{profit.toFixed(4)}</Statistic.Value>
                <Statistic.Label>{t('bill.api.profit')}</Statistic.Label>
              </Statistic>
            </Grid.Column>
          )}
        </Grid>
      </Segment>
    );
  };

  // 渲染账单表格
  const renderBillsTable = (bills, billType, loading, activePage, total, handlePageChange) => {
    const totalPages = billType === 'B' ? getTotalPagesB() : getTotalPagesA();

    return (
      <>
        <Table celled striped sortable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t('bill.col.time')}</Table.HeaderCell>
              <Table.HeaderCell>{t('bill.col.model')}</Table.HeaderCell>
              {admin && <Table.HeaderCell>{t('bill.col.user')}</Table.HeaderCell>}
              <Table.HeaderCell>Task ID</Table.HeaderCell>
              <Table.HeaderCell>{t('bill.api.col.spending_cny')}</Table.HeaderCell>
              <Table.HeaderCell>{t('bill.api.col.group_ratio')}</Table.HeaderCell>
              <Table.HeaderCell>{t('bill.api.col.token_count')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {bills.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={admin ? '7' : '6'} textAlign='center'>{t('bill.no_data')}</Table.Cell>
              </Table.Row>
            ) : (
              bills.map((bill) => (
                <Table.Row key={bill.id}>
                  <Table.Cell>{timestamp2string(bill.created_at)}</Table.Cell>
                  <Table.Cell><code>{bill.model_name}</code></Table.Cell>
                  {admin && <Table.Cell>{bill.username || '-'}</Table.Cell>}
                  <Table.Cell>
                    <code style={{ fontSize: '0.85em', wordBreak: 'break-all' }}>
                      {bill.task_id || '-'}
                    </code>
                  </Table.Cell>
                  <Table.Cell style={{ fontWeight: 'bold', color: '#21ba45' }}>
                    ¥{(bill.quota || 0).toFixed(4)}
                  </Table.Cell>
                  <Table.Cell>{bill.group_ratio}</Table.Cell>
                  <Table.Cell>{bill.prompt_tokens + bill.completion_tokens}</Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table>

        {/* 分页 */}
        {total > ITEMS_PER_PAGE && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1em' }}>
            <Pagination
              activePage={activePage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              size='small'
              firstItem={null}
              lastItem={null}
            />
            <span style={{ marginLeft: '1em', lineHeight: '2em', color: '#666' }}>
              {t('bill.api.pagination_total', { total: total, page: activePage, totalPages: totalPages })}
            </span>
          </div>
        )}
      </>
    );
  };

  // Tab 配置
  const panes = admin
    ? [
        // 管理员可以看到 A/B 两个标签
        {
          menuItem: {
            key: 'bill-b',
            icon: 'user',
            content: t('bill.api.tab_bill_b'),
          },
          render: () => (
            <Tab.Pane loading={loadingB}>
              {renderStats(statsB, 'B')}
              {renderBillsTable(billsB, 'B', loadingB, activePageB, totalB, handlePageChangeB)}
            </Tab.Pane>
          ),
        },
        {
          menuItem: {
            key: 'bill-a',
            icon: 'settings',
            content: t('bill.api.tab_bill_a'),
          },
          render: () => (
            <Tab.Pane loading={loadingA}>
              {renderStats(statsA, 'A')}
              {renderBillsTable(billsA, 'A', loadingA, activePageA, totalA, handlePageChangeA)}
            </Tab.Pane>
          ),
        },
      ]
    : [
        // 普通用户只能看到 B 账单
        {
          menuItem: {
            key: 'bill-b',
            icon: 'user',
            content: t('bill.api.tab_customer_bill'),
          },
          render: () => (
            <Tab.Pane loading={loadingB}>
              {renderStats(statsB, 'B')}
              {renderBillsTable(billsB, 'B', loadingB, activePageB, totalB, handlePageChangeB)}
            </Tab.Pane>
          ),
        },
      ];

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>{t('bill.api.title')}</Card.Header>

          {/* 筛选栏 */}
          <Form style={{ marginTop: '1em', marginBottom: '1em' }}>
            <Form.Group widths='equal'>
              {admin && (
                <Form.Input
                  label={t('bill.filter.username')}
                  placeholder={t('bill.filter.optional')}
                  value={filters.username}
                  onChange={(e) => setFilters({ ...filters, username: e.target.value })}
                />
              )}
              <Form.Input
                label={t('bill.filter.model_name')}
                placeholder={t('bill.filter.optional')}
                value={filters.model_name}
                onChange={(e) => setFilters({ ...filters, model_name: e.target.value })}
              />
              <Form.Input
                label={t('bill.filter.start_time')}
                type='datetime-local'
                value={new Date(filters.start_timestamp * 1000).toISOString().slice(0, 16)}
                onChange={(e) => setFilters({ ...filters, start_timestamp: Math.floor(new Date(e.target.value).getTime() / 1000) })}
              />
              <Form.Input
                label={t('bill.filter.end_time')}
                type='datetime-local'
                value={new Date(filters.end_timestamp * 1000).toISOString().slice(0, 16)}
                onChange={(e) => setFilters({ ...filters, end_timestamp: Math.floor(new Date(e.target.value).getTime() / 1000) })}
              />
              <Form.Field style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button primary onClick={handleSearch} loading={loadingB || loadingA}>{t('bill.filter.search')}</Button>
              </Form.Field>
            </Form.Group>
          </Form>

          {/* A/B 账单标签页 */}
          <Tab
            panes={panes}
            activeIndex={activeTab}
            onTabChange={(e, { activeIndex }) => setActiveTab(activeIndex)}
          />
        </Card.Content>
      </Card>
    </div>
  );
};

export default ApiBill;
